/**
 * store-boot-context — the boot-provided live install/uninstall seam (#116d V2).
 *
 * For an installed artifact's route to go LIVE in-session (the V2 proof), BOTH
 * the composed genome AND the plugin list `App` resolves routes from must update
 * (`resolveRoutes(genome, plugins)` reads the plugin's editors). The studio boot
 * (`main.tsx`) hoists genome + plugins into React state and provides THIS seam:
 * the `/store` surface calls `install(manifest, confirm)`, the boot runs the REAL
 * `@ubiclouds/v5-store` `installArtifact` (recompose + register), then commits
 * `setGenome(next)` + appends the resolved plugin so the new route resolves —
 * no reload.
 *
 * A `null` context (no boot wiring) means the surface renders against the static
 * genome with install disabled — still mountable in isolation (a bare test).
 *
 * @module @ubiclouds/v5-studio/store/store-boot-context
 */

import { createContext, useContext, type ReactNode, type ReactElement } from 'react';
import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';
import type { StoreArtifactManifest, Capability } from '@ubiclouds/v5-store/manifest';
import type { InstallResult } from '@ubiclouds/v5-store/install';

/** The boot-provided Store install/uninstall seam over the LIVE genome+plugins. */
export interface StoreBootContext {
  /** The LIVE composed genome (the boot's `useState` instance). */
  readonly genome: GenomeV3;
  /**
   * Install an artifact into the live genome. `confirm` carries the operator's
   * explicit third-party confirmation (fail-closed when a `third-party` artifact
   * is installed without it). `approvedCapabilities` (#114-PLUGINRT PR6) is the
   * capability set the operator approved at the enumerated confirm — the bounded
   * grant the per-plugin broker is seeded with (`InstallResult.grantedCapabilities`);
   * a requested-but-unapproved capability fail-closes (`capability-ungranted`).
   * Resolves on the recomposed genome; rejects with the `InstallError` on a
   * refusal (the prior genome stands).
   *
   * ASYNC (#279): the boot lazy-imports the Store compose engine off the entry
   * chunk and `await`s it on first use, so this returns a `Promise` — the surface
   * `await`s the recompose before showing its result (the no-reload UX is
   * unchanged, just resolved a microtask later once the engine is warm).
   */
  install(
    manifest: StoreArtifactManifest,
    confirm: boolean,
    approvedCapabilities?: readonly Capability[],
  ): Promise<InstallResult>;
  /** Uninstall an artifact from the live genome (`core` is refused). Async (#279). */
  uninstall(manifest: StoreArtifactManifest): Promise<InstallResult>;
  /** The set of currently-installed artifact ids (for the installed list). */
  readonly installedIds: readonly string[];
}

const Ctx = createContext<StoreBootContext | null>(null);

/** Provide the boot's live install/uninstall seam to the `/store` subtree. */
export function StoreBootProvider({
  value,
  children,
}: {
  readonly value: StoreBootContext | null;
  readonly children: ReactNode;
}): ReactElement {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Read the boot Store seam (`null` when the boot did not wire one). */
export function useStoreBoot(): StoreBootContext | null {
  return useContext(Ctx);
}
